import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { GoogleAdsService } from '@/services/google-ads.service';
import { GoogleAdsAuthService } from '@/services/google-ads-auth.service';
import { GoogleAdsCampaignType } from '@/models/google-ads.model';
import { format } from 'date-fns';
import { GoogleAdsDiagnostics } from './GoogleAdsDiagnostics';
import { DemandGenCampaignForm } from './DemandGenCampaignForm';

export const GoogleAdsManagement: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'integrations' | 'demand-gen' | 'youtube'>('campaigns');
  const [showDemandGenForm, setShowDemandGenForm] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<{ id: string; accountId: string } | null>(null);

  const handleConnectAccount = () => {
    try {
      // Generate state for CSRF protection
      const state = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('google_ads_oauth_state', state);
      
      // Log before initiating
      console.log('[GoogleAdsManagement] Connect Account clicked:', {
        hostname: window.location.hostname,
        origin: window.location.origin,
        state,
        timestamp: new Date().toISOString(),
      });
      
      GoogleAdsAuthService.initiateAuth(state);
    } catch (error: any) {
      console.error('[GoogleAdsManagement] Error initiating OAuth:', error);
      alert(`Error starting OAuth flow: ${error.message}`);
    }
  };

  const { data: campaigns = [], isLoading: campaignsLoading, refetch: refetchCampaigns } = useQuery({
    queryKey: ['googleAdsCampaigns'],
    queryFn: () => GoogleAdsService.getAllCampaigns(),
    enabled: !!profile && activeTab === 'campaigns',
  });

  const { data: integrations = [], isLoading: integrationsLoading, refetch: refetchIntegrations } = useQuery({
    queryKey: ['googleAdsIntegrations'],
    queryFn: async () => {
      console.log('[GoogleAdsManagement] Fetching integrations...');
      const result = await GoogleAdsService.getAllIntegrations();
      console.log('[GoogleAdsManagement] Integrations fetched:', {
        count: result.length,
        integrations: result.map(i => ({ id: i.id, accountId: i.accountId, accountName: i.accountName, isActive: i.isActive })),
      });
      return result;
    },
    enabled: !!profile, // Always fetch when profile is available
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Refetch integrations when component mounts or when returning from OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refresh = urlParams.get('refresh');
    if (refresh === 'true') {
      console.log('[GoogleAdsManagement] Refresh detected in URL, switching to integrations tab and refetching...');
      setActiveTab('integrations');
      // Small delay to ensure tab switch completes before refetch
      setTimeout(() => {
        refetchIntegrations();
      }, 100);
      // Clean up URL params but keep tab
      const cleanUrl = window.location.pathname + window.location.search.replace(/[?&]refresh=true/g, '').replace(/^&/, '?');
      window.history.replaceState({}, '', cleanUrl || window.location.pathname);
    }
  }, [refetchIntegrations]);

  const handleSyncNow = async (integrationId: string, accountId: string) => {
    try {
      console.log('[GoogleAdsManagement] Syncing campaigns for integration:', { integrationId, accountId });
      // TODO: Implement actual sync with Google Ads API
      // For now, just update the lastSyncedAt timestamp
      alert('Sync functionality will be implemented once Google Ads API integration is complete.');
      // await GoogleAdsService.syncCampaignsFromGoogleAds(accountId);
      // refetchIntegrations();
    } catch (error: any) {
      console.error('[GoogleAdsManagement] Error syncing:', error);
      alert(`Error syncing campaigns: ${error.message}`);
    }
  };

  const handleSyncAccountInfo = async (integrationId: string) => {
    try {
      console.log('[GoogleAdsManagement] Syncing account info for integration:', integrationId);
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('@/config/firebase');
      
      const syncAccountInfo = httpsCallable(functions, 'syncGoogleAdsAccountInfo');
      const result = await syncAccountInfo({ integrationId });
      
      const data = result.data as any;
      if (data.success) {
        alert(`Account info synced successfully! Account ID: ${data.accountId}`);
        refetchIntegrations();
      } else {
        alert(`Failed to sync account info: ${data.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('[GoogleAdsManagement] Error syncing account info:', error);
      alert(`Error syncing account info: ${error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      ENABLED: 'bg-green-100 text-green-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      REMOVED: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCampaignTypeIcon = (type: GoogleAdsCampaignType) => {
    const icons: Record<string, string> = {
      SEARCH: '🔍',
      DISPLAY: '🖼️',
      VIDEO: '📹',
      SHOPPING: '🛒',
      PERFORMANCE_MAX: '⚡',
      DISCOVERY: '🔮',
      DEMAND_GEN: '🎯',
      SMART: '🤖',
    };
    return icons[type] || '📊';
  };

  // Filter campaigns by type
  const demandGenCampaigns = campaigns.filter(c => c.type === GoogleAdsCampaignType.DEMAND_GEN || c.isDemandGen);
  const youtubeCampaigns = campaigns.filter(c => c.type === GoogleAdsCampaignType.VIDEO);

  // Calculate stats
  const totalSpent = campaigns.reduce((sum, c) => sum + (c.metrics?.cost || 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0);
  const totalConversions = campaigns.reduce((sum, c) => sum + (c.metrics?.conversions || 0), 0);
  const averageCpc = totalClicks > 0 ? totalSpent / totalClicks : 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Google Ads Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage campaigns, track performance, and optimize your Google Ads investment
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            + Create Campaign
          </button>
          <button
            onClick={handleConnectAccount}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
          >
            Connect Account
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Campaigns</div>
          <div className="text-2xl font-bold text-gray-900">{campaigns.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Spent</div>
          <div className="text-2xl font-bold text-red-600">${totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Clicks</div>
          <div className="text-2xl font-bold text-blue-600">{totalClicks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            ${averageCpc.toFixed(2)} avg CPC
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Conversions</div>
          <div className="text-2xl font-bold text-green-600">{totalConversions.toLocaleString()}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'campaigns' as const, name: 'All Campaigns', count: campaigns.length },
            { id: 'demand-gen' as const, name: 'Demand Gen', count: demandGenCampaigns.length },
            { id: 'youtube' as const, name: 'YouTube Ads', count: youtubeCampaigns.length },
            { id: 'integrations' as const, name: 'Integrations', count: integrations.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          {campaignsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No Google Ads campaigns found</p>
              <button
                onClick={handleConnectAccount}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Connect Google Ads Account
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="text-xl mr-2">{getCampaignTypeIcon(campaign.type)}</span>
                            <p className="text-sm font-medium text-gray-900">
                              {campaign.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              {campaign.type}
                            </span>
                          </div>
                          {campaign.metrics && (
                            <div className="mt-2 flex space-x-4 text-sm text-gray-500">
                              <span>Impressions: {campaign.metrics.impressions.toLocaleString()}</span>
                              <span>Clicks: {campaign.metrics.clicks.toLocaleString()}</span>
                              <span>CTR: {campaign.metrics.ctr.toFixed(2)}%</span>
                              <span>Cost: ${campaign.metrics.cost.toFixed(2)}</span>
                              {campaign.metrics.conversions > 0 && (
                                <span className="text-green-600">
                                  Conversions: {campaign.metrics.conversions}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {campaign.budget && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              Budget: ${campaign.budget.amount.toLocaleString()} / {campaign.budget.period === 'FIXED_DAILY' ? 'fixed daily' : 'daily'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Demand Gen Tab */}
      {activeTab === 'demand-gen' && (
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Google Demand Gen Campaigns</h3>
            <p className="text-sm text-blue-700">
              Demand Gen campaigns use Google's AI to optimize ad placement across YouTube, Discover, and Gmail to reach your target audience at the right moment.
            </p>
          </div>
          
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => {
                // Use first active integration if available
                const activeIntegration = integrations.find(i => i.isActive);
                if (activeIntegration) {
                  setSelectedIntegration({
                    id: activeIntegration.id,
                    accountId: activeIntegration.accountId === 'pending' ? '' : activeIntegration.accountId,
                  });
                  setShowDemandGenForm(true);
                } else {
                  alert('Please connect a Google Ads account first.');
                }
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Create Demand Gen Campaign
            </button>
          </div>

          {demandGenCampaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No Demand Gen campaigns found</p>
              <p className="text-sm text-gray-500 mb-4">
                Create your first Demand Gen campaign to reach audiences across YouTube, Discover, and Gmail.
              </p>
              <button
                onClick={() => {
                  const activeIntegration = integrations.find(i => i.isActive);
                  if (activeIntegration) {
                    setSelectedIntegration({
                      id: activeIntegration.id,
                      accountId: activeIntegration.accountId === 'pending' ? '' : activeIntegration.accountId,
                    });
                    setShowDemandGenForm(true);
                  } else {
                    alert('Please connect a Google Ads account first.');
                  }
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Create Your First Demand Gen Campaign
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {demandGenCampaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-xl">🎯</span>
                            <p className="text-sm font-medium text-gray-900">
                              {campaign.name}
                            </p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                            {campaign.isDemandGen && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Demand Gen
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                            {campaign.budget && (
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Budget: </span>
                                ${campaign.budget.amount.toLocaleString()} / {campaign.budget.period === 'FIXED_DAILY' ? 'fixed daily' : 'daily'}
                              </div>
                            )}
                            {campaign.startDate && (
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">Start: </span>
                                {format(campaign.startDate, 'MMM d, yyyy')}
                              </div>
                            )}
                            {campaign.endDate && (
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">End: </span>
                                {format(campaign.endDate, 'MMM d, yyyy')}
                              </div>
                            )}
                          </div>

                          {campaign.demandGenSettings && (
                            <div className="mt-3 space-y-2">
                              {campaign.demandGenSettings.audienceSignals && campaign.demandGenSettings.audienceSignals.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">Audience Signals:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {campaign.demandGenSettings.audienceSignals.map((signal, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                                      >
                                        {signal}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {campaign.demandGenSettings.creativeAssets && campaign.demandGenSettings.creativeAssets.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">Creative Assets:</p>
                                  <p className="text-xs text-gray-600">
                                    {campaign.demandGenSettings.creativeAssets.length} asset(s) configured
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {campaign.metrics && (
                            <div className="mt-3 flex space-x-4 text-sm text-gray-500">
                              <span>Impressions: {campaign.metrics.impressions.toLocaleString()}</span>
                              <span>Clicks: {campaign.metrics.clicks.toLocaleString()}</span>
                              <span>CTR: {campaign.metrics.ctr.toFixed(2)}%</span>
                              {campaign.metrics.conversions > 0 && (
                                <span className="text-green-600">
                                  Conversions: {campaign.metrics.conversions}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Demand Gen Campaign Form Modal */}
      {showDemandGenForm && (
        <DemandGenCampaignForm
          accountId={selectedIntegration?.accountId}
          onClose={() => {
            setShowDemandGenForm(false);
            setSelectedIntegration(null);
          }}
          onSuccess={() => {
            refetchCampaigns();
            setShowDemandGenForm(false);
            setSelectedIntegration(null);
          }}
        />
      )}

      {/* YouTube Ads Tab */}
      {activeTab === 'youtube' && (
        <div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <h3 className="text-sm font-semibold text-red-900 mb-2">YouTube Video Campaigns</h3>
            <p className="text-sm text-red-700">
              Reach your audience on YouTube with video ads. Track views, engagement, and conversions from your video campaigns.
            </p>
          </div>
          
          {youtubeCampaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No YouTube campaigns found</p>
              <button className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium">
                Create YouTube Campaign
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {youtubeCampaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="text-xl mr-2">📹</span>
                            <p className="text-sm font-medium text-gray-900">
                              {campaign.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </div>
                          {campaign.metrics && campaign.metrics.videoViews && (
                            <div className="mt-2 flex space-x-4 text-sm text-gray-500">
                              <span>Video Views: {campaign.metrics.videoViews.toLocaleString()}</span>
                              <span>View Rate: {campaign.metrics.viewRate?.toFixed(2)}%</span>
                              <span>CPV: ${campaign.metrics.averageCpv?.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Diagnostics Tool */}
          <GoogleAdsDiagnostics />
          
          {integrationsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading integrations...</p>
            </div>
          ) : integrations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No Google Ads accounts connected</p>
              <p className="text-sm text-gray-500 mb-4">
                Connect your Google Ads account to sync campaigns and performance data automatically.
              </p>
              <button
                onClick={handleConnectAccount}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Connect Google Ads Account
              </button>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {integrations.map((integration) => (
                  <li key={integration.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {integration.accountName || 'Unnamed Account'}
                            </p>
                            <span className="text-xs text-gray-500">
                              ID: {integration.accountId || 'N/A'}
                            </span>
                            {integration.accountId === 'pending' && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Pending Account Info
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              integration.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {integration.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Sync Frequency: {integration.syncFrequency || 'daily'}
                            {integration.lastSyncedAt && ` • Last synced: ${format(integration.lastSyncedAt, 'MMM d, yyyy h:mm a')}`}
                            {!integration.lastSyncedAt && ' • Never synced'}
                          </p>
                          {integration.createdAt && (
                            <p className="mt-1 text-xs text-gray-400">
                              Connected: {format(integration.createdAt, 'MMM d, yyyy h:mm a')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => {
                              console.log('[GoogleAdsManagement] Refresh clicked for integration:', integration.id);
                              refetchIntegrations();
                            }}
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                            title="Refresh integrations list"
                          >
                            🔄 Refresh
                          </button>
                          {integration.accountId === 'pending' && (
                            <button
                              onClick={() => handleSyncAccountInfo(integration.id)}
                              className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 text-sm font-medium"
                              title="Fetch account information from Google Ads"
                            >
                              Sync Account Info
                            </button>
                          )}
                          <button
                            onClick={() => handleSyncNow(integration.id, integration.accountId)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                            disabled={integration.accountId === 'pending'}
                            title={integration.accountId === 'pending' ? 'Complete account setup first' : 'Sync campaigns from Google Ads'}
                          >
                            Sync Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

